/**
 * <vimeo-sound-video>
 *
 * Builds a Vimeo player via the official Player API (not a pre-baked iframe) so
 * autoplay, events and unmuting are reliable. It:
 *   - Creates the player when the section first scrolls into view (autoplay-on-scroll).
 *   - Keeps a poster image visible until playback actually starts.
 *   - Adds an optional "Tap for sound" button that unmutes the (muted) autoplay.
 *
 * Required markup:
 *   <vimeo-sound-video data-video-id="123" [data-video-hash="abc"]>
 *     <div data-vimeo-target></div>
 *     <img class="video-section__poster" ...>            (optional)
 *     <button class="video-section__sound">...</button>  (optional)
 *   </vimeo-sound-video>
 */
if (!customElements.get('vimeo-sound-video')) {
  const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';

  const loadVimeoApi = () => {
    if (window.__vimeoApiPromise) return window.__vimeoApiPromise;

    window.__vimeoApiPromise = new Promise((resolve, reject) => {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src="' + VIMEO_API_SRC + '"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = VIMEO_API_SRC;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return window.__vimeoApiPromise;
  };

  class VimeoSoundVideo extends HTMLElement {
    connectedCallback() {
      this.target = this.querySelector('[data-vimeo-target]');
      this.poster = this.querySelector('.video-section__poster');
      this.soundButton = this.querySelector('.video-section__sound');
      this.label = this.querySelector('.video-section__sound-label');
      this.videoId = parseInt(this.dataset.videoId, 10);
      this.videoHash = this.dataset.videoHash || null;
      this.player = null;
      this.initialized = false;

      if (!this.target || !this.videoId) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.observer.disconnect();
              this.init();
            }
          });
        }, { rootMargin: '200px 0px' });
        this.observer.observe(this);
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      loadVimeoApi()
        .then(() => this.createPlayer())
        .catch(() => this.hidePoster());
    }

    createPlayer() {
      const options = {
        autoplay: true,
        muted: true,
        loop: true,
        controls: true,
        background: false,
        playsinline: true,
        responsive: false,
        dnt: true
      };
      if (this.videoHash) {
        options.url = 'https://vimeo.com/' + this.videoId + '/' + this.videoHash;
      } else {
        options.id = this.videoId;
      }

      try {
        this.player = new window.Vimeo.Player(this.target, options);
      } catch (e) {
        this.hidePoster();
        return;
      }

      const reveal = () => this.hidePoster();
      this.player.on('play', reveal);
      this.player.on('playing', reveal);
      this.player.on('timeupdate', reveal);

      this.player.ready()
        .then(() => this.player.setMuted(true))
        .then(() => this.player.play())
        .catch(() => {});

      if (this.soundButton) {
        this.soundButton.addEventListener('click', () => this.enableSound());
        // If the user unmutes via the native controls, retire our button too.
        this.player.on('volumechange', () => {
          this.player.getMuted()
            .then((muted) => { if (!muted) this.hideSoundButton(); })
            .catch(() => {});
        });
      }
    }

    hidePoster() {
      if (this.poster) this.poster.classList.add('is-hidden');
    }

    hideSoundButton() {
      if (this.soundButton) this.soundButton.classList.add('is-hidden');
    }

    enableSound() {
      if (!this.player) return;

      this.player.setMuted(false)
        .then(() => this.player.setVolume(1))
        .then(() => this.player.play())
        .then(() => {
          this.hidePoster();
          this.hideSoundButton();
        })
        .catch(() => {});
    }
  }

  customElements.define('vimeo-sound-video', VimeoSoundVideo);
}
